import {compact, groupByUniqueKey} from '@benchling/js-utils';
import React, {useEffect} from 'react';

import {assert} from 'Asserts';
import {LogLevel} from 'config-migration/migration-log/LogLevel';
import {HighImpactChange, MigrationError, MigrationObjectPlan} from 'config-migration/MigrationConstants';
import {ObjectType} from 'config-migration/types/ObjectType';
import notifications from 'notifications';

import {getActionOrigin, trackTriggeredConfigMigrationErrorToast} from '../../analytics';
import {useConfigMigrationImportTabContext} from '../../contexts/ConfigMigrationImportTabContext';
import {useConfigMigrationPageContext} from '../../contexts/ConfigMigrationPageContext';
import {ObjectAction, ObjectActionType, UpdateAction} from '../../types/objectActions';
import {countChildHighImpactChanges} from '../HighImpactChanges';
import ChildImportPlanSection, {
  ChildImportPlanSectionWrapper,
} from '../import-plan-sections/ChildImportPlanSection';
import {ImportPlanSectionWrapper} from '../import-plan-sections/ImportPlanSection';
import ImportPlanSectionRow, {
  ImportPlanSectionRowWrapper,
  ObjectActionDropdownColumn,
  ObjectDisplayNameColumn,
} from '../import-plan-sections/ImportPlanSectionRow';

export interface ProcessTaskSchemaPlanSectionProps {
  sectionDisplayName: string;
  plans: Array<MigrationObjectPlan>;
  onUpdatePlans: (MigrationObjectPlan: Array<MigrationObjectPlan>) => void;
  collapsed: boolean;
  onUpdateCollapsed: (collapsed: boolean) => void;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

interface MemberObjectPlanAndError {
  memberPlan: MigrationObjectPlan;
  memberErrors: Array<MigrationError>;
}

export interface ProcessTaskSchemaPlanRowProps {
  plan: MigrationObjectPlan;
  memberPlansAndErrors: Array<MemberObjectPlanAndError>;
  errors: Array<MigrationError>;
  onUpdatePlans: (plans: Array<MigrationObjectPlan>) => void;
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

export function deriveFlowchartConfigActionFromTaskSchemaAction(
  taskSchemaPlan: MigrationObjectPlan,
  flowchartConfigPlan: MigrationObjectPlan
): MigrationObjectPlan {
  let flowchartConfigAction: ObjectAction;
  if (
    taskSchemaPlan.selectedAction.type === ObjectActionType.CREATE ||
    taskSchemaPlan.selectedAction.type === ObjectActionType.IGNORE
  ) {
    flowchartConfigAction = {type: taskSchemaPlan.selectedAction.type};
  } else if (taskSchemaPlan.selectedAction.type === ObjectActionType.UPDATE) {
    // FlowchartConfigVersions are immutable. So if the task schema is being updated and the flowchart config
    // version is included in the migration plan, there should not be a provenance URI match.
    flowchartConfigAction = flowchartConfigPlan.initialAction;
  } else {
    assert(false, `Unsupported selected action ${taskSchemaPlan.selectedAction.type}`);
  }
  return {
    ...flowchartConfigPlan,
    selectedAction: flowchartConfigAction,
    childMigrationPlans: [],
  };
}

type PlanWithSelectedUpdateAction = MigrationObjectPlan & {selectedAction: UpdateAction};

function isPlanWithSelectedUpdateAction(plan: MigrationObjectPlan): plan is PlanWithSelectedUpdateAction {
  return plan.selectedAction.type === ObjectActionType.UPDATE;
}

function findCorrespondingUpdateAction(
  taskSchemaPlan: PlanWithSelectedUpdateAction,
  memberPlan: MigrationObjectPlan
): ObjectAction | null {
  // Member objects like output schemas, entry templates, and flowchart configs are defined when the task schema
  // is created. If the action's provenance UI matches the task schema then we're updating a task schema on the
  // destination org that has already been migrated from this task schema. So the member objects must
  // also exist on the destination org and we can select their initial action which will be to update the
  // member object.
  if (taskSchemaPlan.selectedAction.target.provenanceUri === taskSchemaPlan.objectId) {
    return memberPlan.initialAction;
  }
  let correspondingAction: ObjectAction | null =
    memberPlan.additionalActions.find(
      (action) =>
        action.type === taskSchemaPlan.selectedAction.type &&
        action.target.parentIdentifier?.apiIdentifier === taskSchemaPlan.selectedAction.target.apiIdentifier
    ) ?? null;

  if (correspondingAction == null) {
    // If the task schema plan is set to update and there is no corresponding update action for the member plan
    // then we have not yet migrated the member object. So we need to create it instead.
    if (taskSchemaPlan.selectedAction.type === ObjectActionType.UPDATE) {
      correspondingAction = {type: ObjectActionType.CREATE};
    }
  }
  return correspondingAction;
}

export function deriveActionFromTaskSchemaActionForMutableMemberObject(
  taskSchemaPlan: MigrationObjectPlan,
  memberPlan: MigrationObjectPlan
): MigrationObjectPlan {
  let memberAction: ObjectAction;
  if (
    taskSchemaPlan.selectedAction.type === ObjectActionType.CREATE ||
    taskSchemaPlan.selectedAction.type === ObjectActionType.IGNORE
  ) {
    memberAction = {type: taskSchemaPlan.selectedAction.type};
  } else if (isPlanWithSelectedUpdateAction(taskSchemaPlan)) {
    const correspondingAction = findCorrespondingUpdateAction(taskSchemaPlan, memberPlan);
    if (correspondingAction == null) {
      throw new Error(
        `Could not find corresponding ${memberPlan.objectType} action for ${taskSchemaPlan.selectedAction.target.displayName}`
      );
    }
    memberAction = correspondingAction;
  } else {
    assert(false, `Unsupported selected action ${taskSchemaPlan.selectedAction.type}`);
  }
  return {
    ...memberPlan,
    selectedAction: memberAction,
    childMigrationPlans: [],
  };
}

interface MemberPlanConfig {
  title: string;
  deriveUpdateFromTaskSchemaPlan: (
    taskSchemaPlan: MigrationObjectPlan,
    memberPlan: MigrationObjectPlan
  ) => MigrationObjectPlan;
}

const MEMBER_PLAN_CONFIGS_BY_TYPE: {[objectType in ObjectType]?: MemberPlanConfig} = {
  [ObjectType.Template]: {
    title: 'Execution template',
    deriveUpdateFromTaskSchemaPlan: deriveActionFromTaskSchemaActionForMutableMemberObject,
  },
  [ObjectType.ProcessOutputSchema]: {
    title: 'Output schema',
    deriveUpdateFromTaskSchemaPlan: deriveActionFromTaskSchemaActionForMutableMemberObject,
  },
  [ObjectType.ProcessFlowchartConfigVersion]: {
    title: 'Flowchart configuration',
    deriveUpdateFromTaskSchemaPlan: deriveFlowchartConfigActionFromTaskSchemaAction,
  },
};

export function ProcessTaskSchemaPlanRow(props: ProcessTaskSchemaPlanRowProps): JSX.Element {
  const hasErrors = props.errors.filter(({level}) => level === LogLevel.ERROR).length > 0;
  const hasWarnings = props.errors.filter(({level}) => level === LogLevel.WARNING).length > 0;
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(true);

  const {activeTab, eventGroupId} = useConfigMigrationPageContext();
  const {activeStep, destinationOrganizationId} = useConfigMigrationImportTabContext();
  const highImpactChangeCount = countChildHighImpactChanges(props.plan, props.highImpactChangeMap);

  return (
    <>
      <ImportPlanSectionRowWrapper
        plan={props.plan}
        hasErrors={hasErrors}
        hasWarnings={hasWarnings}
        key={props.plan.objectId}
      >
        <ObjectDisplayNameColumn
          hasErrors={hasErrors}
          hasWarnings={hasWarnings}
          plan={props.plan}
          errors={props.errors}
          setIsCollapsed={setIsCollapsed}
          isCollapsed={isCollapsed}
          hasChildren={
            props.plan.childMigrationPlans.length > 0 ||
            (props.memberPlansAndErrors.length > 0 &&
              props.plan.selectedAction.type !== ObjectActionType.IGNORE)
          }
          highImpactChangeCount={highImpactChangeCount}
        />
        <ObjectActionDropdownColumn
          plan={props.plan}
          onUpdatePlan={(updatedPlan) => {
            try {
              const updatedMemberPlans = props.memberPlansAndErrors.map(({memberPlan}) =>
                MEMBER_PLAN_CONFIGS_BY_TYPE[memberPlan.objectType]!.deriveUpdateFromTaskSchemaPlan(
                  updatedPlan,
                  memberPlan
                )
              );
              props.onUpdatePlans([updatedPlan, ...updatedMemberPlans]);
              // eslint-disable-next-line benchlint/always-throw-from-catch
            } catch (e) {
              notifications.error(String(e));
              trackTriggeredConfigMigrationErrorToast({
                action_origin: getActionOrigin(activeTab),
                destination_organization_id: destinationOrganizationId,
                event_group_id: eventGroupId,
                stage: activeTab,
                import_step: activeStep,
              });
            }
          }}
        />
      </ImportPlanSectionRowWrapper>
      {!isCollapsed && props.plan.childMigrationPlans.length > 0 && (
        <ChildImportPlanSection
          plan={props.plan}
          onUpdatePlan={(plan) => {
            props.onUpdatePlans([plan]);
          }}
          highImpactChangeMap={props.highImpactChangeMap}
        />
      )}
      {!isCollapsed &&
        props.plan.selectedAction.type !== ObjectActionType.IGNORE &&
        props.memberPlansAndErrors.map(({memberPlan, memberErrors}) => (
          <ChildImportPlanSectionWrapper
            key={memberPlan.objectId}
            title={MEMBER_PLAN_CONFIGS_BY_TYPE[memberPlan.objectType]!.title}
            dataTestAttrs={{
              component: 'ProcessTaskSchemaPlanRow',
              element: 'memberPlanWrapper',
              key: memberPlan.objectId,
            }}
          >
            <ImportPlanSectionRow
              plan={memberPlan}
              errors={memberErrors}
              onUpdatePlan={(updatedPlan) => {
                props.onUpdatePlans([updatedPlan]);
              }}
              isNested={true}
              showActionInline={true}
              highImpactChangeMap={props.highImpactChangeMap}
            />
          </ChildImportPlanSectionWrapper>
        ))}
    </>
  );
}

function getPlansByTaskSchema(
  plans: Array<MigrationObjectPlan>,
  objectType: ObjectType
): {[key: string]: MigrationObjectPlan} {
  return plans
    .filter((plan) => plan.objectType === objectType)
    .reduce(
      (acc, curr) => {
        const attrs = curr.objectAttributes as {processTaskSchemaId: string};
        assert(attrs.processTaskSchemaId != null, 'Expected processTaskSchemaId attribute to be defined');
        acc[attrs.processTaskSchemaId] = curr;
        return acc;
      },
      {} as {[key: string]: MigrationObjectPlan}
    );
}

export default function ProcessTaskSchemaPlanSection(props: ProcessTaskSchemaPlanSectionProps): JSX.Element {
  const taskSchemaPlans = props.plans.filter((plan) => plan.objectType === ObjectType.ProcessTaskSchema);
  const memberPlanByObjectTypeAndTaskSchema = Object.keys(MEMBER_PLAN_CONFIGS_BY_TYPE).reduce(
    (acc, objectType: ObjectType) => {
      acc[objectType] = getPlansByTaskSchema(props.plans, objectType);
      return acc;
    },
    {} as {[objectType in ObjectType]?: {[key: string]: MigrationObjectPlan}}
  );

  // Templates are hardcoded to "ignore" by default. However when migrating task schemas we expect the entire
  // schema to be migrated at once so we want expect the initial action for the template to match the initial action
  // of the task schema. So here we override the selectedAction of all templates to match the parent task schema
  // plan on the first render.
  useEffect(() => {
    const updatedPlans: Array<MigrationObjectPlan> = [];
    const taskSchemaPlansById = groupByUniqueKey(taskSchemaPlans, (plan) => plan.objectId);
    Object.entries(memberPlanByObjectTypeAndTaskSchema[ObjectType.Template] ?? {}).forEach(
      ([taskSchemaId, templatePlan]) => {
        const taskSchemaPlan = taskSchemaPlansById[taskSchemaId];
        updatedPlans.push(
          deriveActionFromTaskSchemaActionForMutableMemberObject(taskSchemaPlan, templatePlan)
        );
      }
    );
    props.onUpdatePlans(updatedPlans);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedMemberPlanConfigsByType = Object.keys(MEMBER_PLAN_CONFIGS_BY_TYPE).sort(
    (leftObjectType: ObjectType, rightObjectType: ObjectType) =>
      MEMBER_PLAN_CONFIGS_BY_TYPE[leftObjectType]!.title.localeCompare(
        MEMBER_PLAN_CONFIGS_BY_TYPE[rightObjectType]!.title
      )
  );

  const getMemberPlanAndErrors = (taskSchemaPlan: MigrationObjectPlan): Array<MemberObjectPlanAndError> =>
    compact(
      sortedMemberPlanConfigsByType.map((objectType: ObjectType) => {
        const memberPlan = memberPlanByObjectTypeAndTaskSchema[objectType]?.[taskSchemaPlan.objectId];
        if (memberPlan == null) {
          return null;
        }
        return {
          memberPlan,
          memberErrors: props.errorsByObjectId?.[memberPlan.objectId] ?? [],
        };
      })
    );

  return (
    <ImportPlanSectionWrapper {...props} plansToSummarize={taskSchemaPlans}>
      <>
        {taskSchemaPlans.map((taskSchemaPlan) => (
          <ProcessTaskSchemaPlanRow
            key={taskSchemaPlan.objectId}
            plan={taskSchemaPlan}
            memberPlansAndErrors={getMemberPlanAndErrors(taskSchemaPlan)}
            errors={props.errorsByObjectId[taskSchemaPlan.objectId] || []}
            onUpdatePlans={(updatedPlans) => {
              props.onUpdatePlans(updatedPlans);
            }}
            highImpactChangeMap={props.highImpactChangeMap}
          />
        ))}
      </>
    </ImportPlanSectionWrapper>
  );
}
